import { CalendarIcon } from 'lucide-react'
import { useState } from 'react'

import ComboboxMultiSelectFilter, {
  type ComboboxFilterOption,
} from '../../reusable/table/ComboboxMultiSelectFilter'
import FilterManagementDropdown from '../needs_attention/FilterManagementDropdown'
import { filterByLabelAndValueCallback } from '../needs_attention/utils'
import { type SingleSelectedValueFilter } from '../types'

// Generate month options with future and past groupings
const generateMonthOptions = () => {
  const currentDate = new Date()
  const currentMonth = currentDate.getMonth()
  const currentYear = currentDate.getFullYear()

  const futureOptions: ComboboxFilterOption<string>[] = []
  const pastOptions: ComboboxFilterOption<string>[] = []

  // Generate future months (current month + next 11 months)
  for (let i = 0; i < 12; i++) {
    const date = new Date(currentYear, currentMonth + i, 1)
    const isoString = date.toISOString().split('T')[0] // YYYY-MM-DD format
    const displayString = date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
    })
    futureOptions.push({
      value: isoString,
      label: displayString,
    })
  }

  // Generate past months (previous 12 months, ordered from most recent to oldest)
  for (let i = 1; i <= 12; i++) {
    const date = new Date(currentYear, currentMonth - i, 1)
    const isoString = date.toISOString().split('T')[0] // YYYY-MM-DD format
    const displayString = date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
    })
    pastOptions.push({
      value: isoString,
      label: displayString,
    })
  }

  return { futureOptions, pastOptions }
}

const CoverageStartDateFilter = ({
  selectValue,
  selectedValue,
  rangeType,
}: SingleSelectedValueFilter & { rangeType: string }) => {
  const [query, setQuery] = useState('')
  const { futureOptions, pastOptions } = generateMonthOptions()

  // Combine all options for the dropdown trigger display
  const allOptions = [...futureOptions, ...pastOptions]

  const handleSelect = (value: string) => {
    // For single select, if the value is already selected, deselect it
    if (selectedValue === value) {
      selectValue('')
    } else {
      selectValue(value)
    }
  }

  // Filter options based on search query
  const filteredFutureOptions = futureOptions.filter(
    filterByLabelAndValueCallback(query)
  )
  const filteredPastOptions = pastOptions.filter(
    filterByLabelAndValueCallback(query)
  )

  // Convert single value to array format for ComboboxMultiSelectFilter
  const selectedValues = selectedValue ? [selectedValue] : []

  return (
    <div>
      <ComboboxMultiSelectFilter
        selectedValues={selectedValues}
        onSelectValue={handleSelect}
        options={[
          {
            groupLabel: 'Current / Future',
            options: filteredFutureOptions,
          },
          {
            groupLabel: 'Past',
            options: filteredPastOptions,
          },
        ]}
        placeholder={`Search months for ${rangeType.toLowerCase()}...`}
        onValueChange={setQuery}
      >
        <FilterManagementDropdown
          selectedValues={selectedValues}
          options={allOptions}
          noSelectionText={`${rangeType} date`}
          pluralIdentifier='date'
        >
          <CalendarIcon size={20} />
        </FilterManagementDropdown>
      </ComboboxMultiSelectFilter>
    </div>
  )
}

export default CoverageStartDateFilter
